import { QuantitiesTableInnerProps } from '../../models/table-models';
import { useTranslation } from 'react-i18next';
import { ItemQuantity } from '../../models/warehouse-models';
import { useDeleteQuantity } from '../../utils/warehouses-hooks';
import { CustomTable } from '../../components/CustomTable';
import { quantitiesTableHeader, shortQuantitiesTableHeader } from '../../utils/Enums/TableHeaders';
import { AddEditQuantity } from './AddEditQuantity';
import { PaginationComponent } from '../../components/Pagination';
import { NoDataComponent } from '../../components/NoDataComponent';
import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { formatQuantitiesByPassport, transformDateToString } from '../../utils/helper-functions';
import { RedistributeQuantityAction } from './RedistributeQuantity';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';

export const QuantitiesTable = ({
                                    getEntities,
                                    pagination,
                                    refetchEntitiesHook,
                                    warehouseId,
                                    warehouseType,
                                    setSelectedItem,
                                setShowModal,
                            }: QuantitiesTableInnerProps) => {
    const { loggedUser } = useContext(AuthContext);
    const { t } = useTranslation('translation', { keyPrefix: 'table' });

    const { items, pager } = getEntities();
    const [comment, setComment] = useState('');
    const [deleteReason, setDeleteReason] = useState('scrap');
    const isMainWarehouse = !warehouseType;
    const isWriteOffWarehouse = warehouseType === 1;

    const useDelete = async (itemQuantity: ItemQuantity) => {
        await useDeleteQuantity(itemQuantity, deleteReason, comment, loggedUser?.id);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('quantitiesDelete')]} />, toastOptionsTopRight);
    };

    const formatQuantitiesByPassportID = (items: ItemQuantity[]) => {
        return formatQuantitiesByPassport(
            items,
            (item: ItemQuantity) => item.passport.passportId,
            (existingItem: ItemQuantity, newItem: ItemQuantity) => {
                existingItem.count += newItem.count;
                existingItem.cost += newItem.cost;
                if (newItem.availableAmount != null)
                    existingItem.availableAmount = (existingItem.availableAmount ?? 0) + newItem.availableAmount;
                existingItem.collectionOfItems?.push(newItem);
            },
        );
    };


    return (
        <>
            {pager.totalCount > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            groupByField: (item) => item.passport.passport,
                            innerTableFields: [{ field: 'passport.passport' }, { field: 'count' }, { field: 'availableAmount' }, { field: 'batchCode' }, { field: 'cost' }, { field: 'user' }, { field: 'sapCode' }, {field: 'state', isTranslate: true}],
                            collectionOfItems: items,
                            collapsedHeadings: shortQuantitiesTableHeader,
                            headings: quantitiesTableHeader,
                            items: formatQuantitiesByPassportID(items).map(
                                ({ passport, count, availableAmount, user, deliveryDate, dateTime, equipment, sapCode }) => [
                                    passport.passport,
                                    count,
                                    availableAmount != null ? availableAmount : '',
                                    user,
                                    transformDateToString(deliveryDate),
                                    transformDateToString(dateTime),
                                    equipment,
                                    sapCode,
                                ],
                            ),
                        }}
                        accessEditRoles={[Role.WAREHOUSE]}
                        accessActionRoles={[Role.WAREHOUSE]}
                        {...(isMainWarehouse
                            ? {
                                viewComponent: (id: string) => {
                                    const item = items.find((value) => value.id + '' === id);
                                    setSelectedItem((prev) => item);
                                    setShowModal((prev) => true);
                                },
                                editInnerComponent: AddEditQuantity,
                                viewAction: true,
                            }
                            : {})}
                        actionInnerHook={isWriteOffWarehouse ? useDelete : undefined}
                        innerActionComponent={({ item, refetchEntitiesHook }) => (
                            <RedistributeQuantityAction
                                item={item}
                                refetchEntitiesHook={refetchEntitiesHook}
                                warehouseId={warehouseId}
                            />
                        )}
                        comment={comment}
                        setComment={setComment}
                        setDeleteReason={setDeleteReason}
                        deleteButtonText={deleteReason}
                        tableData={formatQuantitiesByPassportID(items)}
                        hasAccordionRow={true}
                        {...{ refetchEntitiesHook }}
                        tableFooterText={(item) => {
                            return `${t('quantity')}: ${item.count.toString()} / ${t('totalPrice')}: ${item.cost.toString()}`;
                        }}
                    />
                    <PaginationComponent
                        recordsCount={pager.totalCount}
                        pagination={{
                            ...pagination,
                            pageSize: pagination.pageSize,
                        }}
                    />
                </>
            ) : (
                <NoDataComponent noDataText={t('quantities')} />
            )}
        </>
    );
};
